import { styled } from "goober";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { zIndexes } from "../lib/zIndexes";
import { Icon } from "./Icon";
import { MenuItem, MenuItemLink } from "./MenuItem";
import { Text } from "./Text";

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

type Props = {
  anchorName: string;
  side: "bottom" | "left" | "right";
  margin: number;
  editTo: string;
  parentNoun: string;
  onRemove: () => void;
  onClose: () => void;
};

export function ConnectorAvatarContextMenu({
  anchorName,
  side,
  margin,
  editTo,
  parentNoun,
  onRemove,
  onClose,
}: Props) {
  const [containerRef, menuRef] = useMouseDownOutside(onClose);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu
        ref={menuRef}
        data-popover
        data-direction={side === "bottom" ? "down" : side}
        style={
          {
            positionAnchor: anchorName,
            ...(side !== "bottom"
              ? {
                  top: "anchor(top)",
                  ...(side === "left"
                    ? { right: "anchor(left)", marginRight: margin }
                    : { left: "anchor(right)", marginLeft: margin }),
                }
              : {
                  top: "anchor(bottom)",
                  right: "anchor(right)",
                  marginTop: margin,
                }),
            positionTryFallbacks:
              "flip-block, flip-inline, flip-block flip-inline",
          } as React.CSSProperties
        }
      >
        <MenuItemLink to={editTo} onClick={onClose}>
          <Icon name="settings-2" size={16} />
          <Text variant="text-regular">Manage connector</Text>
        </MenuItemLink>
        <MenuItem
          onClick={() => {
            onRemove();
            onClose();
          }}
        >
          <Icon name="circle-minus" size={16} />
          <Text variant="text-regular">{`Remove from ${parentNoun}`}</Text>
        </MenuItem>
      </Menu>
    </div>,
    document.body
  );
}
